import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import makeStyles from "@mui/styles/makeStyles";
import OfficeLogo from "../../../../images/Office.svg";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { setUserDetails } from "../../../../actions/auth/authAction";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { setAuthData, getToken } from "../../../../utils/encryption";
import apiBolerplate from "utils/_helpers/apiBolerplate";

const useStyles = makeStyles((theme) => ({
  menu: {
    "& .MuiPaper-root": {
      minWidth: "400px",
      maxHeight: "500px",
      borderRadius: "8px",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    },
  },
  menuHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  entityInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  entityAvatar: {
    width: 48,
    height: 48,
  },
  entityName: {
    fontWeight: 600,
    fontSize: "14px",
    color: theme.palette.text.primary,
  },
  entityMeta: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    marginTop: "2px",
  },
  menuItem: {
    padding: theme.spacing(1.5, 2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  menuItemText: {
    fontSize: "13px",
    fontWeight: 600,
  },
  menuItemIcon: {
    minWidth: "40px",
    color: theme.palette.text.secondary,
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
}));

function EntityPopupMenu({
  anchorEl,
  open,
  onClose,
  profileDetails,
  user,
  selectedEntity,
  onEntitySelect,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user);
  const { parent_entity } = useSelector((state) => state.orgDetails);

  // Demo fetch function - Replace with real API call when ready
  const parentId = user?.parent_id;

  async function fetchOrganizations() {
    const data = await apiBolerplate({
      url: "/entity/get_organisations",
      bodyData: { parent_id: parentId },
      hideLoader: true,
      hideMsg: true,
    });

    return data?.result; // Return the data for further use
  }

  const { data: organizationsList, isLoading } = useQuery({
    queryKey: ["organizationsList", parentId],
    queryFn: fetchOrganizations,
    enabled: open && !!parentId, // Only fetch when popup is open and parentId exists
    // staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      className={classes.menu}
      PaperProps={{
        elevation: 3,
      }}
      sx={{ marginTop: 2 }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "white",
          zIndex: 2,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          padding="8px 16px"
          justifyContent="space-between"
        >
          <Typography
            style={{
              fontWeight: 600,
              fontSize: "16px",
              color: "#24528a",
              // backgroundColor: "#ebf1f8",
              // borderRadius: "4px",
              // margin: "8px",
            }}
          >
            {user?.org_details?.entity_name || "Entity Name"}
          </Typography>
          <Avatar
            src={
              parent_entity?.profile_img?.url ||
              user?.org_details?.profile_img?.url ||
              OfficeLogo
            }
            style={{ width: 32, height: 32, marginRight: 12 }}
          />
        </Box>
        <Divider className={classes.divider} sx={{ color: "#24528a" }} />
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box style={{ padding: "24px", textAlign: "center" }}>
          <CircularProgress size={24} />
          <Typography
            variant="body2"
            style={{ marginTop: "12px", color: "#666" }}
          >
            Loading entities...
          </Typography>
        </Box>
      )}

      {/* Entity Structure */}
      {!isLoading && (
        <>
          <Box
            style={{
              maxHeight: "300px",
              overflowY: "auto",
            }}
            sx={{
              "&::-webkit-scrollbar": {
                width: "10px",
                height: "10px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#fbfbfb",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#e9e9e9",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#bbbbbb",
                },
              },
            }}
          >
            {organizationsList
              ?.slice()
              .sort((a, b) => {
                // Move current entity to the top
                const isACurrent = a?.entity_id === parentId;
                const isBCurrent = b?.entity_id === parentId;
                if (isACurrent && !isBCurrent) return -1;
                if (!isACurrent && isBCurrent) return 1;
                return 0;
              })
              .map((item) => {
                const isCurrentEntity = item?.entity_id === parentId;

                return (
                  <MenuItem
                    key={item?.entity_id}
                    className={classes.menuItem}
                    selected={isCurrentEntity}
                    onClick={() => {
                      
                      // Extract login credentials from the API response
                      const { loginCredentials } = item || {};

                      if (
                        loginCredentials?.accessToken &&
                        loginCredentials?.refreshToken
                      ) {
                        
                        // Use the complete userDetails from loginCredentials
                        const newUserDetails = {
                          ...loginCredentials.userDetails,
                          accessToken: loginCredentials.accessToken,
                          refreshToken: loginCredentials.refreshToken,
                        };

                        // Update Redux store with new user details including tokens
                        dispatch(setUserDetails(newUserDetails));
                        
                        // Store tokens and user data in encrypted localStorage
                        if (typeof window !== "undefined") {
                          try {
                            setAuthData(
                              loginCredentials.accessToken,
                              newUserDetails
                            );
                            
                            // Verify the update
                            const currentEncryptedData =
                              localStorage.getItem("ud");
                            
                          } catch (error) {
                            console.error("❌ Error storing auth data:", error);
                          }
                        }
                      } else {
                        console.warn(
                          "⚠️ No login credentials found for this entity"
                        );

                        // Fallback to the old method if loginCredentials are not available
                        const updatedOrgDetails = {
                          ...currentUser?.org_details,
                          _id: item?.entity_id,
                          entity_name: item?.company_name,
                          name: item?.company_name,
                          profile_img: item?.logo,
                          parent_id: item?.parent_id || item?.entity_id,
                        };

                        const updatedUserDetails = {
                          ...currentUser,
                          entity_id: item?.entity_id,
                          parent_id: item?.entity_id,
                          org_details: updatedOrgDetails,
                        };

                        // Dispatch the action to update user details in Redux
                        dispatch(setUserDetails(updatedUserDetails));

                        // Update localStorage with current token and updated user details
                        if (typeof window !== "undefined") {
                          try {
                            const currentToken = getToken();
                            setAuthData(currentToken, updatedUserDetails);
                            
                          } catch (error) {
                            console.error(
                              "❌ Error updating localStorage (fallback):",
                              error
                            );
                          }
                        }
                      }

                      // Close the menu
                      onClose();

                      // Trigger page reload to ensure all components pick up the new entity data
                      setTimeout(() => {
                        
                        window.location.reload();
                      }, 100);
                    }}
                    sx={{
                      backgroundColor: isCurrentEntity
                        ? "#e3f2fd"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: isCurrentEntity
                          ? "#bbdefb"
                          : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <Avatar
                      src={item.logo.url || OfficeLogo}
                      alt={item.company_name}
                      className={classes.entityAvatar}
                      sx={{ marginRight: 1.5 }}
                    />

                    <ListItemText
                      primary={item?.company_name}
                      secondary={isCurrentEntity ? "Current Entity" : null}
                      primaryTypographyProps={{
                        style: {
                          fontWeight: 600,
                          fontSize: "13px",
                          color: isCurrentEntity ? "#1565c0" : "inherit",
                        },
                      }}
                      secondaryTypographyProps={{
                        style: {
                          fontSize: "11px",
                          color: isCurrentEntity ? "#1565c0" : "inherit",
                          fontWeight: isCurrentEntity ? 500 : "normal",
                        },
                      }}
                    />
                    {isCurrentEntity && (
                      <CheckCircleIcon
                        sx={{
                          fontSize: 18,
                          color: "#1565c0",
                          marginLeft: 1,
                        }}
                      />
                    )}
                  </MenuItem>
                );
              })}

            {/* No entities message */}
            {(!organizationsList || organizationsList.length === 0) && (
              <Box
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#999",
                }}
              >
                <Typography variant="body2">No company available</Typography>
                <Typography variant="body2">Create one</Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              zIndex: 1,
            }}
          >
            <Divider className={classes.divider} sx={{ color: "#24528a" }} />
            <Box display="flex" justifyContent="center" padding="10px">
              <Button
                variant="outlined"
                onClick={() => navigate("/add_new_company")}
              >
                Add New Company
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Menu>
  );
}

export default EntityPopupMenu;
